import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { Button } from '../../components/ui/button';
import { Building2, FileText, CheckCircle2, ArrowRight, Shield } from 'lucide-react';

const SubmitPropertyPage = () => {
  const { t } = useI18n();

  const propertyTypes = [
    'Terrenos',
    'Residencial',
    'Comercial',
    'Industrial',
    'Uso Mixto',
    'Hotelería',
  ];

  const requiredDocs = [
    'Escritura o título de propiedad',
    'Identificación del propietario',
    'Comprobante de domicilio',
    'Predial al corriente',
    'Libertad de gravámenes',
    'Uso de suelo / zonificación',
  ];

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <section className="section-spacing border-b border-border">
        <div className="container-app">
          <div className="max-w-3xl">
            <p className="caption mb-4">Aportación</p>
            <h1 className="heading-1 mb-6">Aportar Inmueble</h1>
            <p className="body-lg">
              Si es propietario de un inmueble con potencial de desarrollo o inversión, 
              puede participar en nuestras estructuras de inversión aportando su activo.
            </p>
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="section-spacing">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="heading-2 mb-8">¿Cómo Funciona?</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm flex-shrink-0">
                    1
                  </span>
                  <div>
                    <h3 className="font-medium mb-2">Registro Inicial</h3>
                    <p className="text-sm text-muted-foreground">
                      Registre su inmueble con información básica: ubicación, tipo, superficie y valor estimado.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="h-8 w-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm flex-shrink-0">
                    2
                  </span>
                  <div>
                    <h3 className="font-medium mb-2">Documentación</h3>
                    <p className="text-sm text-muted-foreground">
                      Suba los documentos requeridos para que podamos evaluar la situación legal y técnica del inmueble.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="h-8 w-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm flex-shrink-0">
                    3
                  </span>
                  <div>
                    <h3 className="font-medium mb-2">Evaluación</h3>
                    <p className="text-sm text-muted-foreground">
                      Nuestro equipo revisa la información y determina si el inmueble cumple con nuestros criterios.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="h-8 w-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm flex-shrink-0">
                    4
                  </span>
                  <div>
                    <h3 className="font-medium mb-2">Propuesta</h3>
                    <p className="text-sm text-muted-foreground">
                      Si es aprobado, presentamos una propuesta de términos indicativos para la aportación.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <span className="h-8 w-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm flex-shrink-0">
                    5
                  </span>
                  <div>
                    <h3 className="font-medium mb-2">Formalización</h3>
                    <p className="text-sm text-muted-foreground">
                      Firmamos los contratos necesarios y formalizamos la aportación del inmueble.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12">
                <Link to="/register">
                  <Button className="btn-primary gap-2">
                    Comenzar Ahora
                    <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="space-y-8">
              {/* Property Types */}
              <div className="card-premium">
                <div className="flex items-center gap-3 mb-6">
                  <Building2 className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <h3 className="font-medium">Tipos de Inmuebles</h3>
                </div>
                <ul className="space-y-3">
                  {propertyTypes.map((type, index) => (
                    <li key={index} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                      {type}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Required Documents */}
              <div className="card-premium">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <h3 className="font-medium">Documentos Requeridos</h3>
                </div>
                <ul className="space-y-3">
                  {requiredDocs.map((doc, index) => (
                    <li key={index} className="flex items-center gap-3 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-primary/50" strokeWidth={1.5} />
                      {doc}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-4">
                  * Pueden requerirse documentos adicionales según el tipo de inmueble y estructura de propiedad.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-card">
        <div className="container-app">
          <div className="text-center mb-16">
            <p className="caption mb-4">Beneficios</p>
            <h2 className="heading-2">¿Por Qué Aportar su Inmueble?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-primary" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-medium mb-3">Estructura Profesional</h3>
              <p className="text-sm text-muted-foreground">
                Su inmueble se integra a una estructura legal robusta con gobernanza de grado corporativo: comunicación constante, procesos transparentes y vehículos jurídicos adecuados.
              </p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-8 w-8 text-primary" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-medium mb-3">Acceso a Capital</h3>
              <p className="text-sm text-muted-foreground">
                Facilite el desarrollo de su propiedad con capital de inversionistas calificados.
              </p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-primary" strokeWidth={1} />
              </div>
              <h3 className="text-lg font-medium mb-3">Participación en Resultados</h3>
              <p className="text-sm text-muted-foreground">
                Mantenga participación en los rendimientos del proyecto desarrollado.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing">
        <div className="container-app text-center">
          <h2 className="heading-3 mb-6">¿Tiene un Inmueble que Aportar?</h2>
          <p className="body-text max-w-2xl mx-auto mb-8">
            Regístrese en nuestra plataforma para iniciar el proceso de evaluación de su propiedad.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="btn-primary">
                Registrarse
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-outline">
                Contactar
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubmitPropertyPage;
